/** Alex conversation setup: ONE fetch-or-create call through the shared conversations HTTP client, dispatching into local messaging state. */

import { type Dispatch, type SetStateAction, type MutableRefObject } from 'react';
import { userSafeError } from '@federa/utils/errors/error-core';
import { DEFAULT_CONVERSATION_TYPE } from '@federa/const/assistant';
import { DEFAULT_MAX_RETRIES } from '@federa/const/timing';
import { REDUCER_ACTION_TYPES } from '@federa/const/reducer-actions';
import { DEFAULT_GUEST_ROLE, PLACEHOLDER_USERNAME } from '@federa/const/auth-session';
import { ACTIVE_BRAND } from '@federa/const/brand-registry';
import { LOGGER_TAGS } from '@federa/const/logging';
import { isGuestUser } from '@federa/utils/strings/user-id';
import { createConversation } from '@federa/components/messaging/messaging';
import { scopedLogger } from '@federa/utils/deps/registry';
import { isRecordValue } from '@federa/const/record-guards';
import { nonEmptyStringOrUndefined } from '@federa/const/string-guards';
import { clearCachedAssistantRoom, readCachedAssistantRoom, writeCachedAssistantRoom } from '@federa/utils/assistant/assistant-room-cache';
import { persistGuestSessionToken } from './ws-credential.ts';
import { isFreshAssistantRoom } from './assistant-room-freshness.ts';
import type { ChatRoomStruct } from '@federa/types/conversations-chat-structs';
import type { AppDispatch } from '@federa/types/react-app-state';
import type { ConversationSetupParams } from '@federa/types/ui-chat-messaging';

const log = scopedLogger(LOGGER_TAGS.ASSISTANT_CONTEXT);

/** The brand half is composed once, so the guest and named arms cannot drift on a re-skin. */
const ASSISTANT_CONVERSATION_LABEL = `${ACTIVE_BRAND.shortName} Assistant`;

function generateConversationFriendlyName(username: string | undefined): string {
	return isGuestUser(username ?? '')
		? ASSISTANT_CONVERSATION_LABEL
		: `${username || PLACEHOLDER_USERNAME} - ${ASSISTANT_CONVERSATION_LABEL}`;
}

/** Normalize a room response to ensure it has the correct type */
function normalizeRoomResponse(room: ChatRoomStruct): ChatRoomStruct {
	return {
		...room,
		conversationType: room.conversationType || DEFAULT_CONVERSATION_TYPE,
	};
}

/** Empty the open conversation's thread and participant list -- the reset a conversation switch and a user change
 *  share. Each dispatch writes its own messaging-state key, so the two land the same in either order. */
export function clearOpenConversation(dispatch: AppDispatch): void {
	dispatch({ type: REDUCER_ACTION_TYPES.MESSAGING_SET_MESSAGES, payload: [] });
	dispatch({
		type: REDUCER_ACTION_TYPES.MESSAGING_UPDATE,
		payload: { currentParticipants: [] },
	});
}

function clearUserState(dispatch: AppDispatch, setCurrentConversation: Dispatch<SetStateAction<ChatRoomStruct | null>>) {
	log.debug('User changed, clearing previous user data');
	setCurrentConversation(null);
	clearOpenConversation(dispatch);
	dispatch({ type: REDUCER_ACTION_TYPES.MESSAGING_SET_ROOMS, payload: [] });
}

/** What a completed setup learned about the room it settled on: which room, whether this open MINTED it, and the room
 *  ticket the server handed back with it (absent when the room was adopted rather than created). */
interface CompletedRoom {
	readonly room: ChatRoomStruct;
	readonly isNew: boolean;
	readonly ticket?: string;
}

/** Setup is OVER, whichever way it ended: the in-flight guard is released and the pane stops initializing. ONE
 *  spelling of that ending, so a success arm and a failure arm cannot come to release different halves of it --
 *  releasing only the guard leaves the pane spinning forever, and only the spinner lets a second setup start. */
function endSetup(p: ConversationSetupParams): void {
	p.isSettingUpRef.current = false;
	p.setIsInitializing(false);
}

function completeSetup(p: ConversationSetupParams, { room, isNew, ticket }: CompletedRoom) {
	// Remembered for the NEXT open, which then dials the room's socket before this read has even been issued -- with
	// the room ticket the server minted, so that dial verifies one signature instead of the whole Firebase path.
	writeCachedAssistantRoom(p.userId, room, ticket);
	applyRoom(p, room, isNew);
}

/** The state side of a completed setup, WITHOUT touching the cache: adopting the remembered room must not rewrite the
 *  record it was read from, because a ticket-less rewrite wiped the ticket a heartbeat before the socket read it
 *  (measured 2026-09-03: every open still presented the Firebase JWT, and the cache held a ticket only AFTER). */
function applyRoom(p: ConversationSetupParams, room: ChatRoomStruct, isNew: boolean) {
	p.setCurrentConversation(room);
	if (isNew) p.dispatch({ type: REDUCER_ACTION_TYPES.MESSAGING_ADD_ROOM, payload: room });
	p.previousUserIdRef.current = p.userId;
	endSetup(p);
}

function completeWithError(p: ConversationSetupParams, err: unknown, defaultMsg: string) {
	const errDetails = isRecordValue(err)
		? {
				message: (err as { message?: string }).message,
				status: (err as { status?: number }).status,
				raw: (err as { raw?: unknown }).raw,
			}
		: err;
	log.error('Failed to setup conversation:', errDetails);
	p.setError(userSafeError(err, defaultMsg));
	endSetup(p);
}

/** Read cancellation flag (defeats TS narrowing for mutable refs across await). */
function isCancelled(ref: { current: boolean }): boolean {
	return ref.current;
}

/** Persist the guest session token from guest-chat-create so guest chat survives reload; a no-op for authed users. */
function persistGuestSessionTokenFromResponse(raw: unknown): void {
	const token = (raw as { guest_session_token?: string } | null | undefined)?.guest_session_token;
	if (token) persistGuestSessionToken(token);
}

/**
 * HEAD START, THEN TRUTH -- and the truth is ONE call. A room remembered on this device is adopted at once (the socket
 * dials while the server is still being asked), and the server is asked through `room/create`, which is fetch-or-create
 * by construction: its existing-lookup arm answers the caller's assistant room when one exists and mints one when none
 * does. The conversations LIST this used to read first (~1s of a new user's first open, 2026-09-03) decided nothing
 * the create call does not, and the g4 switcher that shows the list reads it through its own hook.
 * With a room adopted, a failed create is logged rather than shown: the panel is already working.
 */
export async function performConversationSetup(p: ConversationSetupParams) {
	p.setIsInitializing(true);
	p.setError(null);
	if (p.userChanged) clearUserState(p.dispatch, p.setCurrentConversation);

	const cached = adoptCachedAssistantRoom(p);
	try {
		await ensureAlexConversation(p, cached);
	} catch (err) {
		if (isCancelled(p.cancelledRef)) return;
		if (cached) log.warn('Room ensure failed after adopting the cached assistant room', { error: userSafeError(err, '') });
		else completeWithError(p, err, 'Failed to setup conversation');
	}
}

/**
 * Whether a host's WARM signal (`assistant-warm-signal`: intent on the Assistant control, or an idle pre-warm) may open
 * the pane for this viewer: ONLY when a room is already remembered on this device. Setup is fetch-or-create, so a warm
 * for a viewer with no room would CREATE one -- for a guest, a minted guest session and a conversation row -- on a hover
 * that never became a click. A remembered room means warming only re-adopts what the viewer already has; a first-timer
 * pays the create on the open they actually make. The null-user arm is the signed-out shell before an identity exists.
 */
export function warmMayOpen(userId: string | null): boolean {
	return userId !== null && readCachedAssistantRoom(userId) !== null;
}

/** The head start: the room remembered on this device, adopted at once when there is one. */
function adoptCachedAssistantRoom(p: ConversationSetupParams): ChatRoomStruct | null {
	const cached = readCachedAssistantRoom(p.userId);
	if (cached) applyRoom(p, cached, false);
	return cached;
}

/** The truth: the fetch-or-create answer. It confirms the adoption when it names the cached room, replaces it when it
 *  names another (a deleted room included), and is the whole setup when nothing was cached. */
async function ensureAlexConversation(p: ConversationSetupParams, cached: ChatRoomStruct | null): Promise<void> {
	const createResult = await createConversation(
		{
			userId: p.userId,
			role: p.role || DEFAULT_GUEST_ROLE,
			friendlyName: generateConversationFriendlyName(p.username),
			conversationType: DEFAULT_CONVERSATION_TYPE,
		},
		p.transport,
	);
	if (isCancelled(p.cancelledRef)) return;
	if (!createResult.success) {
		if (cached) throw createResult;
		completeWithError(p, createResult, 'Failed to create conversation');
		return;
	}
	persistGuestSessionTokenFromResponse(createResult.raw);
	const room = normalizeRoomResponse(createResult.data);
	const ticket = readRoomTicketFromResponse(createResult.raw);
	if (cached?.id === room.id) {
		// Adoption confirmed; only the ticket is new, and the next dial (a reconnect, the next open) should carry it.
		writeCachedAssistantRoom(p.userId, room, ticket);
		return;
	}
	if (cached) clearCachedAssistantRoom();
	completeSetup(p, { room, isNew: isFreshAssistantRoom(room), ticket });
}

/** The room ticket `room/create` answers a named caller (`ws_ticket`); undefined for a guest or a pre-ticket server. */
function readRoomTicketFromResponse(raw: unknown): string | undefined {
	return nonEmptyStringOrUndefined((raw as { ws_ticket?: unknown } | null | undefined)?.ws_ticket);
}

/** Check if conversation setup should be skipped */
export function shouldSkipSetup(
	auth: { isAuthInitialized: boolean },
	options: {
		user: { userId: string | null };
		isSettingUpRef: MutableRefObject<boolean>;
		previousUserIdRef: MutableRefObject<string | null>;
		currentConv: ChatRoomStruct | null;
		hasError: boolean;
		retryCountRef: MutableRefObject<number>;
	},
): boolean {
	const { user, isSettingUpRef, previousUserIdRef, currentConv, hasError, retryCountRef } = options;
	if (!auth.isAuthInitialized || !user.userId) return true;
	if (isSettingUpRef.current) {
		log.debug('Already setting up, skipping');
		return true;
	}
	if (previousUserIdRef.current === user.userId && currentConv) {
		log.debug('Same user with existing conversation, skipping setup');
		return true;
	}
	if (currentConv && currentConv.conversationType !== DEFAULT_CONVERSATION_TYPE) {
		log.debug('Current conversation is not alex, skipping alex setup');
		return true;
	}
	if (hasError && retryCountRef.current >= DEFAULT_MAX_RETRIES) {
		log.debug('Max retries reached, skipping setup');
		return true;
	}
	return false;
}
